// Formatting for prices and dates on the pricing table and billing page

// The browser's locale, if Intl accepts it (some report tags like "en-US@posix")
export const browserLocale = () => {
    const tag = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || 'en-US';
    try {
        return Intl.getCanonicalLocales(tag)[0] || 'en-US';
    } catch (e) {
        return 'en-US';
    }
};

// Whole amounts without cents ($29, not $29.00); JPY never has decimals
export const formatMoney = (amount, currency) => {
    try {
        return new Intl.NumberFormat(browserLocale(), {
            style: 'currency',
            currency: currency.toUpperCase(),
            maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
            minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        }).format(amount);
    } catch (e) {
        return `${amount} ${currency.toUpperCase()}`;
    }
};

export const currencyLabel = (currency) => {
    try {
        const symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), currencyDisplay: 'narrowSymbol' })
            .formatToParts(0).find(part => part.type === 'currency')?.value;
        return symbol && symbol !== currency.toUpperCase() ? `${currency.toUpperCase()} ${symbol}` : currency.toUpperCase();
    } catch (e) {
        return currency.toUpperCase();
    }
};

export const formatLongDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(browserLocale(), { month: 'long', day: 'numeric', year: 'numeric' });
};

export const daysUntil = (value) => {
    if (!value) return null;
    return Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86400000));
};

// A plan picked on the landing page before signing in, picked up once they're in the app
const INTENT_KEY = 'ls-plan-intent';
export const savePlanIntent = (plan, interval) => {
    try {
        sessionStorage.setItem(INTENT_KEY, JSON.stringify({ plan, interval }));
    } catch (e) {
        // Storage unavailable; they can pick the plan again in the app
    }
};
export const takePlanIntent = () => {
    try {
        const intent = JSON.parse(sessionStorage.getItem(INTENT_KEY) || 'null');
        sessionStorage.removeItem(INTENT_KEY);
        return intent;
    } catch (e) {
        return null;
    }
};

// The last billing and pricing answers, shown straight away on the next visit while fresh ones load
export const readCached = (key) => {
    try {
        return JSON.parse(localStorage.getItem(`ls-cache:${key}`) || 'null');
    } catch (e) {
        return null;
    }
};
// On sign out, so the next person on this browser doesn't see this account's plan
export const clearCached = () => {
    try {
        Object.keys(localStorage).filter(key => key.startsWith('ls-cache:billing:')).forEach(key => localStorage.removeItem(key));
    } catch (e) {
        // Storage unavailable; nothing was cached
    }
};

export const writeCached = (key, value) => {
    try {
        localStorage.setItem(`ls-cache:${key}`, JSON.stringify(value));
    } catch (e) {
        // Storage unavailable; the next visit just loads from the server
    }
};
